import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Share, Plus, Home, Download, X, ChevronRight } from 'lucide-react';
import { usePWA } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

interface IOSInstallGuideProps {
  onClose?: () => void;
}

const IOSInstallGuide: React.FC<IOSInstallGuideProps> = ({ onClose }) => {
  const { isSafari, isIOSChrome, dismissIOSPrompt } = usePWA();

  const handleClose = () => {
    dismissIOSPrompt();
    onClose?.();
  };

  if (isIOSChrome) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center pb-4">
          <div className="mx-auto mb-3 p-3 bg-blue-100 dark:bg-blue-900/50 rounded-full w-fit">
            <Download className="h-6 w-6 text-blue-600 dark:text-blue-400" />
          </div>
          <CardTitle className="text-lg">Install QWS Portal App</CardTitle>
          <p className="text-sm text-muted-foreground">
            Chrome on iOS doesn't support PWA installation. Please use Safari instead.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
              <Badge variant="secondary" className="mt-0.5 text-xs">1</Badge>
              <div className="flex-1">
                <p className="text-sm font-medium">Copy this URL</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Tap the button below to copy the current page URL
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
              <Badge variant="secondary" className="mt-0.5 text-xs">2</Badge>
              <div className="flex-1">
                <p className="text-sm font-medium">Open Safari</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Paste the URL in Safari browser
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
              <Badge variant="secondary" className="mt-0.5 text-xs">3</Badge>
              <div className="flex-1">
                <p className="text-sm font-medium">Install from Safari</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Follow the Safari installation steps
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-2 pt-4">
            <Button 
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
              }}
              className="flex-1"
            >
              Copy URL
            </Button>
            <Button variant="outline" onClick={handleClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center pb-4">
        <div className="mx-auto mb-3 p-3 bg-blue-100 dark:bg-blue-900/50 rounded-full w-fit">
          <Download className="h-6 w-6 text-blue-600 dark:text-blue-400" />
        </div>
        <CardTitle className="text-lg">Install QWS Portal App</CardTitle>
        <p className="text-sm text-muted-foreground">
          Add to your home screen for easy access and native app experience
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3">
          <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
            <Badge variant="secondary" className="mt-0.5 text-xs">1</Badge>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium">Tap the Share button</p>
                <Share className="h-4 w-4 text-blue-600" />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Look for the share icon in Safari's toolbar (usually at the bottom)
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
            <Badge variant="secondary" className="mt-0.5 text-xs">2</Badge>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium">Select "Add to Home Screen"</p>
                <Plus className="h-4 w-4 text-green-600" />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Scroll down in the share menu to find this option
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
            <Badge variant="secondary" className="mt-0.5 text-xs">3</Badge>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium">Tap "Add"</p>
                <Home className="h-4 w-4 text-green-600" />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Confirm the installation and find the app on your home screen
              </p>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
          <div className="flex items-start gap-2">
            <div className="p-1 bg-blue-100 dark:bg-blue-900/50 rounded">
              <ChevronRight className="h-3 w-3 text-blue-600" />
            </div>
            <div>
              <p className="text-xs font-medium text-blue-900 dark:text-blue-100">
                Pro Tip
              </p>
              <p className="text-xs text-blue-700 dark:text-blue-200 mt-1">
                Once installed, the app will work offline and feel like a native iOS app!
              </p>
            </div>
          </div>
        </div>

        <Button onClick={handleClose} variant="outline" className="w-full">
          Got it, thanks!
        </Button>
      </CardContent>
    </Card>
  );
};

export default IOSInstallGuide;
